import React, { useState } from "react";
import {
  FlaskConical,
  Car,
  HelpCircle,
  Mail,
  ChevronDown,
  ChevronRight,
  FileText,
  Users,
} from "lucide-react";

const EmailViewer = () => (
  <div className="bg-stone-50 rounded-lg border border-stone-200 p-4 text-xs font-mono text-stone-600 h-64 overflow-y-auto custom-scrollbar leading-relaxed">
    <div className="mb-4 border-b border-stone-200 pb-2">
      <div className="font-bold text-stone-800">
        Subject: Re: Decade Protocol
      </div>
      <div>From: Matthew Quin &lt;mattquin11@gmail.com&gt;</div>
      <div>Date: Sunday, Dec 21, 2025</div>
    </div>

    <p className="mb-4">
      "Thanks Ben,
      <br />
      Lots of great info and insight here. We will certainly have lots to talk
      about at our next meeting..."
    </p>

    <div className="pl-4 border-l-2 border-stone-200 mb-4">
      <div className="font-bold text-stone-800 mb-1">
        On Dec 21, 2025, Benton Belcher wrote:
      </div>
      <p>
        "I wanted to explore whether I could build tools that support different
        phases of business planning..."
      </p>
      <p className="mt-2 text-stone-500 italic">
        [...See full build notes on Supplements as Execution Layer...]
      </p>
    </div>

    <div className="pl-4 border-l-2 border-indigo-100 bg-indigo-50/50 p-2 rounded mb-4">
      <div className="font-bold text-indigo-900 mb-1">
        Matt's Protocol Draft (The "Meat"):
      </div>
      <p>
        <strong>Program Mission:</strong> Measurable biological age reversal of
        10–15 years.
      </p>
      <p>
        <strong>Core Stack:</strong> NMN, Spermidine, Taurine, Berberine.
      </p>
      <p>
        <strong>Phase 1:</strong> System Lock (Creatine, Omega-3, Mag).
      </p>
      <p>
        <strong>Phase 2:</strong> Optimization (Berberine, NMN).
      </p>
      <p>
        <strong>Phase 3:</strong> Advanced (Methylene Blue, Fisetin pulses).
      </p>
      <p className="mt-2">
        <strong>Key Insight:</strong> "Supplements are not the product.
        Supplements are the execution layer of the system."
      </p>
    </div>
  </div>
);

const DecadeProtocolCard = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showEmail, setShowEmail] = useState(false);

  return (
    <div className="mb-6 border-l-4 border-indigo-600 bg-white rounded-r-xl shadow-sm overflow-hidden transition-all">
      {/* HEADER */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="p-5 border-b border-stone-100 bg-indigo-50/30 cursor-pointer hover:bg-indigo-50/50 transition-colors flex justify-between items-center"
      >
        <div>
          <div className="flex items-center gap-2 text-indigo-700 mb-1">
            <FlaskConical className="w-4 h-4" />
            <span className="text-xs font-bold uppercase tracking-widest">
              Side Project • Strategy
            </span>
          </div>
          <h2 className="text-lg font-bold text-stone-800">
            🧪 The Decade Protocol
          </h2>
          {!isExpanded && (
            <div className="mt-1 flex items-center gap-2 text-xs font-medium text-stone-500">
              <HelpCircle className="w-3 h-3" />
              <span>Status: Gathering Info / Role Undefined</span>
            </div>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-stone-400" />
        ) : (
          <ChevronRight className="w-5 h-5 text-stone-400" />
        )}
      </div>

      {/* EXPANDED CONTENT */}
      {isExpanded && (
        <div className="p-5 space-y-6 animate-in slide-in-from-top-2">
          {/* 1. THE ERIC UPDATE (Strategic Ambiguity) */}
          <section className="bg-amber-50 rounded-lg p-4 border border-amber-100">
            <div className="flex items-start gap-3">
              <div className="bg-amber-100 p-2 rounded-full text-amber-600 mt-1">
                <Car className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-stone-800 mb-1">
                  Monday Morning Drive-By (w/ Eric)
                </h3>
                <div className="text-xs text-stone-600 space-y-2 leading-relaxed">
                  <p>
                    <strong>The Update:</strong> Eric ran into you leaving the
                    neighborhood. He plans to respond to all emails and schedule
                    a post-holiday connect.
                  </p>
                  <p>
                    <strong>The Hesitation:</strong> Eric is{" "}
                    <em>not 100% sure</em> there is enough info to move forward
                    as a group entity yet.
                  </p>
                  <p className="bg-white p-2 rounded border border-amber-200 font-medium text-amber-800">
                    ❓ <strong>Role Risk:</strong> This might remain a "Matt
                    Project" where your role is purely vendor support
                    (Marketing/App Dev) rather than Co-Founder.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* 2. THE MATT PROTOCOL (Tactical Depth) */}
          <section>
            <div className="flex items-center gap-2 mb-3 text-indigo-600">
              <FileText className="w-4 h-4" />
              <h3 className="text-xs font-bold uppercase tracking-widest">
                Matt's Latest Protocol Data
              </h3>
            </div>

            <div className="text-sm text-stone-600 mb-3">
              Matt sent a detailed breakdown of the protocol structure,
              acknowledging the "Execution Layer" concept. He provided specific
              stacks (Morning/Evening) and tiered phases.
            </div>

            <button
              onClick={() => setShowEmail(!showEmail)}
              className="flex items-center gap-2 w-full justify-center p-2 border border-stone-200 rounded-lg text-xs font-bold text-stone-500 hover:bg-stone-50 transition-colors"
            >
              <Mail className="w-3 h-3" />
              {showEmail ? "Hide Email Thread" : "View Full Email Chain"}
            </button>

            {showEmail && (
              <div className="mt-3 animate-in fade-in">
                <EmailViewer />
              </div>
            )}
          </section>

          {/* 3. NEXT STEPS */}
          <section className="border-t border-stone-100 pt-4">
            <div className="flex items-center gap-2 mb-2 text-stone-400">
              <Users className="w-4 h-4" />
              <h3 className="text-xs font-bold uppercase tracking-widest">
                Next Action
              </h3>
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-stone-400"></div>
              <span className="text-sm font-medium text-stone-600">
                Wait for Eric/Matt to schedule post-holiday meeting.
              </span>
            </div>
          </section>
        </div>
      )}
    </div>
  );
};

export default DecadeProtocolCard;
